import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Layout } from '@layout/Layout';
import usePlanReportStore, { Period } from '@/store/hr/PlanReportStore';
import usePlanBoardStore from '@/store/hr/PlanBoardStore';
import PeriodSelector from '@components/hr/planReports/PeriodSelector';
import ExportMenu from '@components/hr/planReports/ExportMenu';
import { TaskStatusChip } from '@components/hr/planReports/TaskStatusChip';

const SlippagePage: React.FC = () => {
  const slippage = usePlanReportStore((s) => s.slippage);
  const loading = usePlanReportStore((s) => s.loading);
  const error = usePlanReportStore((s) => s.error);
  const fetchSlippage = usePlanReportStore((s) => s.fetchSlippage);

  const board = usePlanBoardStore((s) => s.board);
  const fetchBoard = usePlanBoardStore((s) => s.fetchBoard);

  const [period, setPeriod] = useState<Period | null>(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (!board) fetchBoard({});
  }, [board, fetchBoard]);

  useEffect(() => {
    if (period) fetchSlippage({ from: period.from, to: period.to, minCarry: 1 });
  }, [period?.from, period?.to, fetchSlippage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePeriod = useCallback((p: Period) => setPeriod(p), []);

  const aging = slippage?.agingTasks || [];
  const chronic = slippage?.chronicSlippers || [];
  const stale = slippage?.staleOpen || [];
  const reasons = slippage?.reasons;

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Slippage &amp; Blockers
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Work that keeps getting pushed, and the reasons people give for it.
            </Typography>
          </Box>
          <ExportMenu
            report="slippage"
            filters={{ from: period?.from, to: period?.to, minCarry: 1 }}
            disabled={!period || aging.length === 0}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <PeriodSelector today={board?.today} onChange={handlePeriod} />
        </Box>

        <Card sx={{ borderRadius: 2 }}>
          <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="scrollable" sx={{ px: 2, pt: 1 }}>
            <Tab label={`Aging tasks (${aging.length})`} sx={{ textTransform: 'none' }} />
            <Tab label={`Chronic slippers (${chronic.length})`} sx={{ textTransform: 'none' }} />
            <Tab label={`Blockers (${reasons?.total_incomplete ?? 0})`} sx={{ textTransform: 'none' }} />
            <Tab label={`Stale open (${stale.length})`} sx={{ textTransform: 'none' }} />
          </Tabs>

          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* ── Aging tasks ─────────────────────────────────────────── */}
                {tab === 0 &&
                  (aging.length === 0 ? (
                    <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography>Nothing is slipping in this period.</Typography>
                    </Box>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Employee</TableCell>
                            <TableCell>Task</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">
                              <Tooltip title="How many times this task has been pushed to another day. This counts EVENTS, not consecutive days.">
                                <span>Carried</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Calendar days since it was first planned. Not the same as the carry count — a task can sit idle and be picked up weeks later.">
                                <span>Age</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>Latest reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {aging.map((t) => (
                            <TableRow key={t.id} hover>
                              <TableCell>
                                <Typography sx={{ fontWeight: 600 }}>{t.employee_name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {t.dept_name || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ maxWidth: 320 }}>
                                <Typography sx={{ fontWeight: 600 }}>{t.title}</Typography>
                                {t.is_stale && (
                                  <Chip
                                    label="Stale"
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    sx={{ height: 18, mt: 0.5 }}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <TaskStatusChip status={t.status} />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={`${t.carry_count}×`}
                                  size="small"
                                  color={t.carry_count >= 3 ? 'warning' : 'default'}
                                  sx={{ fontWeight: 700 }}
                                />
                              </TableCell>
                              <TableCell align="center">{t.age_days}d</TableCell>
                              <TableCell sx={{ maxWidth: 280 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  {t.incomplete_reason || '—'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ))}

                {/* ── Chronic slippers ────────────────────────────────────── */}
                {tab === 1 &&
                  (chronic.length === 0 ? (
                    <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography>
                        Nobody has a task pushed {slippage?.thresholds.chronic_carry ?? 3}+ times.
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Employee</TableCell>
                            <TableCell>Department</TableCell>
                            <TableCell align="center">Chronic tasks</TableCell>
                            <TableCell align="center">Worst carry</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {chronic.map((c) => (
                            <TableRow key={c.employee_id} hover>
                              <TableCell sx={{ fontWeight: 600 }}>{c.employee_name}</TableCell>
                              <TableCell>{c.dept_name || '—'}</TableCell>
                              <TableCell align="center">{c.chronic_tasks}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={`${c.worst_carry_count}×`}
                                  size="small"
                                  color="warning"
                                  sx={{ fontWeight: 700 }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ))}

                {/* ── Blockers ────────────────────────────────────────────── */}
                {tab === 2 && (
                  <Box sx={{ p: 3 }}>
                    <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                      These groups are guessed from keywords in the free text, so some will be
                      filed wrong. The verbatim reasons under each group are the actual evidence —
                      read those before drawing any conclusion about a person.
                    </Alert>

                    {(reasons?.incomplete_buckets || []).length === 0 ? (
                      <Typography color="text.secondary">
                        No "why not completed" reasons in this period.
                      </Typography>
                    ) : (
                      (reasons?.incomplete_buckets || []).map((b) => (
                        <Accordion key={b.key} disableGutters sx={{ mb: 1, borderRadius: 2 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ width: '100%', pr: 2 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: 0.5
                                }}
                              >
                                <Typography sx={{ fontWeight: 600 }}>{b.label}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {b.count} · {Math.round(b.share * 100)}%
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={b.share * 100}
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography variant="caption" color="text.secondary">
                              What people actually wrote:
                            </Typography>
                            {b.samples.map((s, i) => (
                              <Typography key={i} variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                                “{s}”
                              </Typography>
                            ))}
                          </AccordionDetails>
                        </Accordion>
                      ))
                    )}
                  </Box>
                )}

                {/* ── Stale open ──────────────────────────────────────────── */}
                {tab === 3 && (
                  <Box>
                    <Alert severity="warning" sx={{ m: 3, borderRadius: 2 }}>
                      These days were never closed out, and the daily gate only looks back{' '}
                      {slippage?.thresholds.stale_after_days ?? 7} days — so nobody will ever be
                      asked about them again. Their tasks will stay open forever unless someone
                      intervenes.
                    </Alert>
                    {stale.length === 0 ? (
                      <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography>No stranded tasks. Everything has been accounted for.</Typography>
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Employee</TableCell>
                              <TableCell>Task</TableCell>
                              <TableCell>Planned on</TableCell>
                              <TableCell align="center">Age</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {stale.map((t) => (
                              <TableRow key={t.id} hover>
                                <TableCell sx={{ fontWeight: 600 }}>{t.employee_name}</TableCell>
                                <TableCell>{t.title}</TableCell>
                                <TableCell>{t.plan_date}</TableCell>
                                <TableCell align="center">{t.age_days}d</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Layout>
  );
};

export default SlippagePage;
